/**
 * Bunker's half of the shared notifications (owner's decision 21.10.2026): settings and pop-ups
 * live in the platform shell, in one place for everything. Inside the shell's Chat frame Bunker
 * tells the host
 *   - how many unread messages and mentions it has, so the «Бункер» item can show them;
 *   - every pop-up it would show itself if it were on screen, so the shell can show it instead;
 * and listens for whether the shell shows the frame at all (lib/shellVisibility.ts).
 *
 * Desktop gets the same counts through `bunker:set-badge` (lib/desktopBadgeSync.ts; desktop/main.js
 * relays them to the shell page) and draws its own card (desktop/notifyOverlay.js), so this is
 * frame only. Outside the frame every function is a no-op.
 */
import { useAuthStore } from '@/stores/authStore';
import { useDataStore } from '@/stores/dataStore';
import { announceNotification, announceUnread, isPlatformEmbedded, onHostVisibility, type HostNotification } from './platformEmbed';
import { setShellShowsBunker } from './shellVisibility';
import { readUnreadTotals } from './unreadTotals';

const platformId = () => useAuthStore.getState().user?.platformId ?? null;

/** Starts the frame side. Returns the stop; a no-op outside the frame. */
export function installShellNotifications(): () => void {
  if (!isPlatformEmbedded()) return () => undefined;
  let last = '';
  const report = (force = false) => {
    const { total, mentions } = readUnreadTotals();
    const owner = platformId();
    const key = `${owner ?? ''}:${total}:${mentions}`;
    if (!force && key === last) return;
    last = key;
    announceUnread(total, mentions, owner);
  };
  report(true);
  const offData = useDataStore.subscribe(() => report());
  // Another account in the frame must not keep the previous one's numbers on the host.
  const offAuth = useAuthStore.subscribe(() => report());
  const offVisibility = onHostVisibility((visible) => {
    setShellShowsBunker(visible);
    // The host has just attached or come back: it gets the numbers now, not on the next message.
    report(true);
  });
  return () => { offData(); offAuth(); offVisibility(); };
}

/** Hand the shell a pop-up (frame only). */
export function notifyShell(notification: HostNotification): void {
  if (!isPlatformEmbedded()) return;
  announceNotification(notification, platformId());
}
